/**
 * useLens -- Semantic Fingerprint Lens for the Cosmic Map
 * =======================================================
 * Takes one or more "probe" projects and the full project list. Returns:
 *   - lensResults:  ranked array of { project, warmth (0-1), bridgeWord }
 *   - warmthMap:    Map<projectId, warmth> for fast lookup during render
 *   - bridgeWords:  rare words the probes share with each other (multi-probe
 *                   only) -- i.e. what makes a collection a set
 *   - isLensActive: boolean
 *
 * HOW IT WORKS
 *   1. Extract each project's "fingerprint" -- meaningful words from its
 *      name, description and tags, lowercased and deduplicated.
 *   2. Score every candidate by counting shared words with each probe.
 *   3. Weight rare words higher -- a word shared by only 2 projects is far
 *      more interesting than one shared by 30 (IDF-inspired).
 *   4. With several probes, average the per-probe scores. A candidate that
 *      resembles the whole set beats one that resembles a single member.
 *   5. Boost "disciplinary surprise" -- a candidate whose zone is outside
 *      every probe's zone scores higher, because that is the jump Enume
 *      exists to celebrate.
 *   6. For each match, identify the rarest bridge word -- the shared word
 *      appearing in the fewest projects globally.
 *
 * SINGLE VS MULTI PROBE
 *   activateWithProject(project)    -- one probe. Original behaviour.
 *   activateWithProjects([a, b, c]) -- lens a collection.
 *   Accepts project objects or bare ids.
 *
 * PERFORMANCE
 *   Fingerprints and the global word-frequency map depend only on the
 *   project list, so they are memoized separately from scoring. Changing
 *   the probe no longer rebuilds the corpus.
 *
 * Place at: frontend/src/hooks/useLens.js
 */

import { useMemo, useState, useCallback } from 'react';
import { getProjectZone } from '../config/zones';

// -- Stopwords: common words that add noise, not signal -------------------
const STOP_WORDS = new Set([
  'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
  'of', 'with', 'by', 'from', 'is', 'it', 'this', 'that', 'was', 'are',
  'be', 'has', 'had', 'have', 'do', 'does', 'did', 'will', 'would',
  'could', 'should', 'may', 'might', 'can', 'not', 'no', 'so', 'if',
  'my', 'i', 'me', 'we', 'you', 'he', 'she', 'they', 'its', 'our',
  'your', 'their', 'been', 'being', 'about', 'more', 'some', 'than',
  'very', 'just', 'also', 'into', 'over', 'such', 'up', 'out', 'how',
  'all', 'each', 'which', 'when', 'what', 'there', 'use', 'used',
  'using', 'make', 'made', 'like', 'new', 'get', 'got', 'one', 'two',
  'first', 'project', 'created', 'based', 'way', 'well',
]);

// Minimum word length to consider
const MIN_WORD_LENGTH = 3;

// Maximum number of lens results to return (keeps the map readable)
const MAX_RESULTS = 25;

// Cross-zone surprise multiplier
const CROSS_ZONE_BOOST = 1.5;

// Minimum warmth score to include in results (filters noise)
const WARMTH_THRESHOLD = 0.08;

// Minimum MEAN raw score the best match must clear before the lens returns
// anything at all. Warmth is normalized against the top hit, so without an
// absolute floor the best result is always 1.0 -- even when it shares one
// unremarkable word. Scale reference: a word appearing in 2 projects scores
// ~0.63, in 4 projects ~0.43, in 30 projects ~0.29.
// TUNE THIS against real data once there are enough projects to judge.
const MIN_ABSOLUTE_SCORE = 0.30;

// Bridge words shared BETWEEN probes (multi-probe only)
const MAX_BRIDGE_WORDS = 4;
const MIN_BRIDGE_SHARE = 2;   // a word must appear in >= this many probes


// -- Word extraction ------------------------------------------------------

/**
 * Extract meaningful words from a project's name, description and tags.
 * Returns a Set of lowercase strings.
 */
function extractFingerprint(project) {
  const words = new Set();

  const addText = (text) => {
    if (!text) return;
    const tokens = text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, ' ')
      .split(/\s+/)
      .filter(w => w.length >= MIN_WORD_LENGTH && !STOP_WORDS.has(w));
    tokens.forEach(w => words.add(w));
  };

  addText(project.name);
  addText(project.description);

  // Tags are the most deliberate signal -- always include
  const tags = project.tags || [];
  tags.forEach(tag => {
    const t = (typeof tag === 'string' ? tag : tag.name || '').trim().toLowerCase();
    if (t && t.length >= 2) {
      words.add(t);
      // Also split multi-word tags into individual words
      if (t.includes(' ') || t.includes('-')) {
        t.split(/[\s-]+/).forEach(part => {
          if (part.length >= MIN_WORD_LENGTH && !STOP_WORDS.has(part)) {
            words.add(part);
          }
        });
      }
    }
  });

  return words;
}


// -- Global word frequency ------------------------------------------------

/**
 * Build a map of word -> count of projects containing that word.
 * Used for rarity weighting: words in fewer projects score higher.
 */
function buildWordFrequency(allFingerprints) {
  const freq = new Map();
  allFingerprints.forEach(fp => {
    fp.words.forEach(word => {
      freq.set(word, (freq.get(word) || 0) + 1);
    });
  });
  return freq;
}


// -- Scoring --------------------------------------------------------------

/**
 * Score a candidate against a SINGLE probe's word set.
 * Zone handling deliberately lives in the caller, which knows about all
 * probes; a multi-probe lens has no single zone to compare against.
 * Returns { rawScore, bridgeWord, bridgeWordFreq, sharedWords, sharedCount }
 * or null when nothing is shared.
 */
function scoreCandidate(probeWords, candidateFP, wordFreq) {
  const candidateWords = candidateFP.words;
  const shared = [];

  probeWords.forEach(word => {
    if (candidateWords.has(word)) {
      shared.push(word);
    }
  });

  if (shared.length === 0) return null;

  // Score each shared word by rarity: rarer words contribute more.
  // IDF-inspired: score = 1 / log2(frequency + 1)
  let rawScore = 0;
  let rarestWord = null;
  let rarestFreq = Infinity;

  shared.forEach(word => {
    const freq = wordFreq.get(word) || 1;
    rawScore += 1 / Math.log2(freq + 1);

    if (freq < rarestFreq) {
      rarestFreq = freq;
      rarestWord = word;
    }
  });

  return {
    rawScore,
    bridgeWord: rarestWord,
    bridgeWordFreq: rarestFreq,
    sharedWords: shared,
    sharedCount: shared.length,
  };
}


/**
 * Rare words the probes share with EACH OTHER. With a single probe there is
 * nothing to bridge, so this returns []. With a collection it answers
 * "what makes these things a set?" -- usable as chips under the title.
 */
function computeBridgeWords(probeFPs, wordFreq) {
  if (probeFPs.length < 2) return [];

  const counts = new Map();
  probeFPs.forEach(fp => {
    fp.words.forEach(word => {
      counts.set(word, (counts.get(word) || 0) + 1);
    });
  });

  const shared = [];
  counts.forEach((memberCount, word) => {
    if (memberCount >= MIN_BRIDGE_SHARE) {
      shared.push({
        word,
        memberCount,
        globalFreq: wordFreq.get(word) || 1,
      });
    }
  });

  // Shared by more members first; ties broken by global rarity.
  shared.sort((a, b) => (b.memberCount - a.memberCount) || (a.globalFreq - b.globalFreq));
  return shared.slice(0, MAX_BRIDGE_WORDS);
}


// -- Hook -----------------------------------------------------------------

export default function useLens(projects = []) {
  const [probeProjectIds, setProbeProjectIds] = useState([]);

  const activateWithProject = useCallback((project) => {
    if (!project) return;
    const id = typeof project === 'object' ? project.id : project;
    if (id === null || id === undefined) return;
    setProbeProjectIds([id]);
  }, []);

  const activateWithProjects = useCallback((items) => {
    const ids = (items || [])
      .map(p => (p && typeof p === 'object' ? p.id : p))
      .filter(id => id !== null && id !== undefined);
    if (ids.length === 0) return;
    setProbeProjectIds(Array.from(new Set(ids)));
  }, []);

  const deactivate = useCallback(() => {
    setProbeProjectIds([]);
  }, []);

  // Backwards-compatible single-id setter.
  const setProbeProjectId = useCallback((id) => {
    setProbeProjectIds(id === null || id === undefined ? [] : [id]);
  }, []);

  // -- Corpus: depends only on the project list ---------------------------
  const corpus = useMemo(() => {
    const fingerprints = projects.map(p => ({
      project: p,
      words: extractFingerprint(p),
      zoneId: getProjectZone(p).id,
    }));
    const wordFreq = buildWordFrequency(fingerprints);
    const byId = new Map(fingerprints.map(fp => [fp.project.id, fp]));
    return { fingerprints, wordFreq, byId };
  }, [projects]);

  // -- Scoring: depends on the probe set ----------------------------------
  const results = useMemo(() => {
    const empty = {
      lensResults: [],
      warmthMap: new Map(),
      probeProject: null,
      probeProjects: [],
      bridgeWords: [],
    };

    if (!probeProjectIds.length || !projects.length) return empty;

    const probeFPs = probeProjectIds
      .map(id => corpus.byId.get(id))
      .filter(Boolean);

    if (!probeFPs.length) return empty;

    const probeProjects = probeFPs.map(fp => fp.project);
    const probeIdSet = new Set(probeFPs.map(fp => fp.project.id));
    const probeZones = new Set(probeFPs.map(fp => fp.zoneId).filter(Boolean));
    const bridgeWords = computeBridgeWords(probeFPs, corpus.wordFreq);

    const base = {
      ...empty,
      probeProject: probeProjects[0] || null,
      probeProjects,
      bridgeWords,
    };

    // Probes with no extractable words can't contribute a score.
    const usable = probeFPs.filter(fp => fp.words.size > 0);
    if (!usable.length) return base;

    const scored = [];

    corpus.fingerprints.forEach(candidateFP => {
      if (probeIdSet.has(candidateFP.project.id)) return;

      let total = 0;
      let matched = 0;
      let best = null;

      usable.forEach(probeFP => {
        const r = scoreCandidate(probeFP.words, candidateFP, corpus.wordFreq);
        if (!r) return;
        total += r.rawScore;
        matched += 1;
        if (!best || r.rawScore > best.rawScore) best = r;
      });

      if (!best) return;

      // MEAN across ALL usable probes -- non-matching probes contribute 0,
      // so broad resemblance to the whole set outranks a single strong hit.
      let rawScore = total / usable.length;

      // Disciplinary surprise: the candidate sits outside every probe's zone.
      const isCrossZone = Boolean(
        candidateFP.zoneId && probeZones.size && !probeZones.has(candidateFP.zoneId)
      );
      if (isCrossZone) rawScore *= CROSS_ZONE_BOOST;

      scored.push({
        project: candidateFP.project,
        zoneId: candidateFP.zoneId,
        isCrossZone,
        rawScore,
        matchedProbes: matched,
        probeCount: usable.length,
        bridgeWord: best.bridgeWord,
        bridgeWordFreq: best.bridgeWordFreq,
        sharedWords: best.sharedWords,
        sharedCount: best.sharedCount,
      });
    });

    if (scored.length === 0) return base;

    scored.sort((a, b) => b.rawScore - a.rawScore);

    // Absolute floor: if even the best match is weak, say nothing rather
    // than lighting the map up on a coincidence.
    if (scored[0].rawScore < MIN_ABSOLUTE_SCORE) return base;

    // Normalize to warmth (0-1) relative to the top score.
    const maxScore = scored[0].rawScore;
    scored.forEach(s => {
      s.warmth = maxScore > 0 ? s.rawScore / maxScore : 0;
    });

    const filtered = scored
      .filter(s => s.warmth >= WARMTH_THRESHOLD)
      .slice(0, MAX_RESULTS);

    // warmthMap for O(1) lookup during render.
    const warmthMap = new Map();
    probeIdSet.forEach(id => warmthMap.set(id, 1.0));
    filtered.forEach(s => {
      warmthMap.set(s.project.id, s.warmth);
    });

    return {
      lensResults: filtered,
      warmthMap,
      probeProject: probeProjects[0] || null,
      probeProjects,
      bridgeWords,
    };
  }, [probeProjectIds, projects, corpus]);

  return {
    // State
    isLensActive: probeProjectIds.length > 0,
    probeProjectId: probeProjectIds.length ? probeProjectIds[0] : null,
    probeProjectIds,
    probeProject: results.probeProject,
    probeProjects: results.probeProjects,
    lensResults: results.lensResults,
    warmthMap: results.warmthMap,
    bridgeWords: results.bridgeWords,

    // Actions
    activateWithProject,
    activateWithProjects,
    deactivate,
    setProbeProjectId,
    setProbeProjectIds,
  };
}
